/* eslint-disable @typescript-eslint/no-unsafe-call */
'use client';

import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Grid,
  Card,
  CardContent,
  Box,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Avatar,
  CircularProgress,
  SelectChangeEvent,
  Alert,
  Tabs,
  Tab,
  Badge,
  Divider,
  useTheme,
  useMediaQuery,
  Stack,
  Paper,
} from '@mui/material';
import {
  Instagram,
  Facebook,
  Twitter,
  Settings,
  Send,
  Share,
  Image as ImageIcon,
  Edit,
  Delete,
  Sync,
  AutoAwesome,
  PhotoLibrary,
  Dashboard,
} from '@mui/icons-material';

// Custom Threads Icon Component - 실제 Threads 공식 아이콘
const ThreadsIcon = ({ sx, ...props }: { sx?: any; [key: string]: any }) => (
  <svg
    width='20'
    height='20'
    viewBox='0 0 192 192'
    fill='currentColor'
    style={{ ...sx }}
    {...props}
    role='img'
    aria-label='Threads'
  >
    <path d='M141.537 88.9883C140.71 88.5919 139.87 88.2104 139.019 87.8451C137.537 60.5382 122.616 44.905 97.5619 44.745C97.4484 44.7443 97.3355 44.7443 97.222 44.7443C82.2364 44.7443 69.7731 51.1409 62.102 62.7807L75.881 72.2328C81.6116 63.5383 90.6052 61.6848 97.2286 61.6848C97.3051 61.6848 97.3819 61.6848 97.4576 61.6855C105.707 61.7381 111.932 64.1366 115.961 68.814C118.893 72.2193 120.854 76.925 121.825 82.8638C114.511 81.6207 106.601 81.2385 98.145 81.7233C74.3247 83.0954 59.0111 96.9879 60.0396 116.292C60.5615 126.084 65.4397 134.508 73.775 140.011C80.8224 144.663 89.899 146.938 99.3323 146.423C111.79 145.74 121.563 140.987 128.381 132.296C133.559 125.696 136.834 117.143 138.28 106.366C144.217 109.949 148.617 114.664 151.047 120.332C155.179 129.967 155.42 145.8 142.501 158.708C131.182 170.016 117.576 174.908 97.0135 175.059C74.2042 174.89 56.9538 167.575 45.7381 153.317C35.2355 139.966 29.8077 120.682 29.6052 96C29.8077 71.3178 35.2355 52.0336 45.7381 38.6827C56.9538 24.4249 74.2039 17.11 97.0132 16.9405C119.988 17.1113 137.539 24.4614 149.184 38.788C154.894 45.8136 159.199 54.6488 162.037 64.9503L178.184 60.6422C174.744 47.9622 169.331 37.0357 161.965 27.974C147.036 9.60668 125.202 0.195148 97.0695 0H96.9569C68.8816 0.19447 47.2921 9.6418 32.7883 28.0793C19.8819 44.4864 13.2244 67.3157 13.0007 95.9325L13 96L13.0007 96.0675C13.2244 124.684 19.8819 147.514 32.7883 163.921C47.2921 182.358 68.8816 191.806 96.9569 192H97.0695C122.03 191.827 139.624 185.292 154.118 170.811C173.081 151.866 172.51 128.119 166.26 113.541C161.776 103.087 153.227 94.5962 141.537 88.9883ZM98.4405 129.507C88.0005 130.095 77.1544 125.409 76.6196 115.372C76.2232 107.93 81.9158 99.626 99.0812 98.6368C101.047 98.5234 102.976 98.468 104.871 98.468C111.106 98.468 116.939 99.0737 122.242 100.233C120.264 124.935 108.662 128.946 98.4405 129.507Z' />
  </svg>
);
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';
import { toast } from 'sonner';
import { apiGetReviews, Review, ReviewListParams } from '../../api/review.api';
import {
  socialAccountApi,
  socialPostApi,
  oauthApi,
  SocialAccount as APISocialAccount,
  SocialPlatform,
  CreateSocialPostRequest,
  PostStatus,
} from '../../api/social.api';
import { gptPromptApi, GPTPrompt } from '../../api/gpt.api';
import SocialAccountConnector from '../../components/SocialAccountConnector';
import { facebookStatusCache } from '../../utils/facebookStatusCache';
import BlogEditor from '../../components/BlogEditor';
import Config from '../../config/constants';

interface SocialAccount {
  platform: SocialPlatform;
  username: string;
  password: string;
  isConnected: boolean;
}

interface ReviewWithPosting extends Review {
  isPosted?: boolean;
  postedAt?: string;
  postedPlatforms?: string[];
}

interface PostContent {
  text: string;
  images: string[];
  hashtags: string[];
}

interface ReviewImageSelection {
  reviewId: number;
  selectedImages: string[];
}

const DATE_RANGE_OPTIONS = [
  { value: '1', label: '오늘' },
  { value: '7', label: '최근 7일' },
  { value: '30', label: '최근 30일' },
  { value: '90', label: '최근 90일' },
  { value: 'all', label: '전체' },
];

const SOCIAL_PLATFORMS = [
  {
    key: SocialPlatform.FACEBOOK,
    name: 'Facebook',
    icon: Facebook,
    color: '#1877F2',
    maxLength: 63206,
    description: 'Facebook, Instagram, Threads 통합 연결',
  },
];

const ReviewManage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // 사용자 정보 가져오기
  const getBranchIdFromUser = () => {
    try {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        const user = JSON.parse(storedUser);
        console.log('📍 저장된 사용자 정보:', user);
        // user.branchInfo.id 또는 user.branch_id를 branchId로 사용
        const branchId = user.branchInfo?.id || user.branch_id;
        console.log('📍 브랜치 ID 추출:', { 
          userId: user.id, 
          branchInfoId: user.branchInfo?.id, 
          branchId: user.branch_id,
          finalBranchId: branchId 
        });
        return branchId;
      }
    } catch (error) {
      console.error('사용자 정보 파싱 오류:', error);
    }
    return null;
  };

  // URL 파라미터 또는 localStorage에서 branchId 가져오기
  const urlBranchId = Number(searchParams.get('branchId'));
  const userBranchId = getBranchIdFromUser();
  const branchId = urlBranchId || userBranchId;

  // 상태 관리
  const [loading, setLoading] = useState(false);
  const [reviews, setReviews] = useState<ReviewWithPosting[]>([]);
  const [selectedReviews, setSelectedReviews] = useState<number[]>([]);
  const [filters, setFilters] = useState<ReviewListParams>({
    branchId: branchId || 0,
    dateRange: '30',
    serviceType: '',
    searchText: '',
  });

  // 다이얼로그 상태
  const [socialAccountsOpen, setSocialAccountsOpen] = useState(false);
  const [postEditorOpen, setPostEditorOpen] = useState(false);

  // 소셜 계정 정보 (Facebook 통합 시스템)
  const [socialAccounts, setSocialAccounts] = useState<SocialAccount[]>([
    {
      platform: SocialPlatform.FACEBOOK,
      username: '',
      password: '',
      isConnected: false,
    },
    {
      platform: SocialPlatform.THREADS,
      username: '',
      password: '',
      isConnected: false,
    },
  ]);

  // 포스트 내용
  const [postContent, setPostContent] = useState<PostContent>({
    text: '',
    images: [],
    hashtags: ['#맛집', '#리뷰', '#추천'],
  });

  // 리뷰별 이미지 선택 상태
  const [selectedReviewImages, setSelectedReviewImages] = useState<
    ReviewImageSelection[]
  >([]);

  // 선택된 이미지 URL 목록 (갤러리용)
  const [selectedImages, setSelectedImages] = useState<string[]>([]);

  // 선택된 포스팅 플랫폼
  const [selectedPlatforms, setSelectedPlatforms] = useState<
    (SocialPlatform | 'meta-integration' | 'threads')[]
  >([]);

  // 현재 탭 상태
  const [currentTab, setCurrentTab] = useState<
    'workflow' | 'gallery' | 'dashboard' | 'editor' | 'manager'
  >(() => {
    const path = window.location.pathname;
    return path.includes('posting-manage') ? 'manager' : 'workflow';
  });

  // 소셜 계정 다이얼로그 탭 상태
  const [socialAccountTab, setSocialAccountTab] = useState<
    'accounts' | 'prompts'
  >('accounts');

  // 플랫폼별 포스팅 조회를 위한 상태
  const [platformFilter, setPlatformFilter] = useState<
    'all' | 'Facebook' | 'Instagram' | 'Threads'
  >('all');
  const [postedContents, setPostedContents] = useState<any[]>([]);

  // Instagram 2차 인증 관련 상태
  const [instagramTwoFactorOpen, setInstagramTwoFactorOpen] = useState(false);
  const [pendingInstagramCredentials, setPendingInstagramCredentials] =
    useState<{ username: string; password: string } | null>(null);
  const [twoFactorCode, setTwoFactorCode] = useState('');

  // 포스팅 수정 관련 상태
  const [editPostOpen, setEditPostOpen] = useState(false);
  const [editingPost, setEditingPost] = useState<any>(null);
  const [editPostContent, setEditPostContent] = useState<PostContent>({
    text: '',
    images: [],
    hashtags: [],
  });

  // 동기화 관련 상태
  const [syncing, setSyncing] = useState(false);

  // GPT 포스팅 생성 관련 상태
  const [gptPrompts, setGptPrompts] = useState<GPTPrompt[]>([]);
  const [selectedPrompt, setSelectedPrompt] = useState<string>('');
  const [gptGenerating, setGptGenerating] = useState(false);
  const [gptPromptType, setGptPromptType] = useState<'diary' | 'promotion'>(
    'diary',
  );

  // AI 자동 생성 로딩 상태
  const [aiGenerating, setAiGenerating] = useState(false);

  useEffect(() => {
    if (!branchId || branchId === 0) {
      toast.error('올바르지 않은 접근입니다. 로그인이 필요합니다.');
      return;
    }

    setFilters((prev) => ({ ...prev, branchId }));
    fetchReviews();

    if (!socialAccounts.some((acc) => acc.isConnected)) {
      fetchSocialAccounts();
    }

    if (currentTab === 'manager') {
      fetchPostedContents();
    }
  }, [branchId, currentTab]);

  const fetchSocialAccounts = async (forceRefresh: boolean = false) => {
    try {
      console.log('🎯 소셜 계정 상태 확인 시작...', { branchId, forceRefresh });

      const facebookStatus = await facebookStatusCache.getStatus(
        branchId,
        forceRefresh,
      );

      let facebookConnected = false;
      let facebookUsername = '';

      if (facebookStatus) {
        facebookConnected = facebookStatus.isConnected;
        facebookUsername = facebookStatus.userInfo?.name || 'Facebook 사용자';
      }

      let threadsConnected = false;
      let threadsUsername = '';

      try {
        const threadsResponse = await fetch(
          `${Config.API_HOST}/api/threads/status`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ userId: branchId }),
          },
        );

        if (threadsResponse.ok) {
          const threadsData = await threadsResponse.json();
          threadsConnected = threadsData.isConnected || false;
          threadsUsername = threadsData.username || '';
        }
      } catch (threadsError) {
        console.error('❌ Threads 상태 확인 오류:', threadsError);
      }

      const updatedAccounts = [
        {
          platform: SocialPlatform.FACEBOOK,
          username: facebookConnected ? facebookUsername : '',
          password: '',
          isConnected: facebookConnected,
        },
        {
          platform: SocialPlatform.THREADS,
          username: threadsConnected ? threadsUsername : '',
          password: '',
          isConnected: threadsConnected,
        },
      ];

      setSocialAccounts(updatedAccounts);
      console.log('🎯 소셜 계정 상태 업데이트 완료:', updatedAccounts);
    } catch (error) {
      console.error('소셜 계정 조회 오류:', error);
    }
  };

  useEffect(() => {
    if (filters.branchId && filters.branchId !== 0) {
      fetchReviews();
    }
  }, [filters]);

  const fetchReviews = async () => {
    try {
      setLoading(true);
      const response = await apiGetReviews({
        ...filters,
        page: 1,
        limit: 100,
      });

      const reviewsWithPosting = response.data.map((review) => ({
        ...review,
        isPosted: review.isPosted || false,
        postedAt: review.postedAt,
        postedPlatforms: review.postedPlatforms || [],
      }));

      setReviews(reviewsWithPosting);
    } catch (error) {
      console.error('리뷰 조회 오류:', error);
      toast.error('리뷰 조회에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange =
    (field: keyof ReviewListParams) => (event: SelectChangeEvent<string>) => {
      setFilters((prev) => ({
        ...prev,
        [field]: event.target.value,
      }));
    };

  const handleReviewSelect = (reviewId: number) => {
    const newSelectedReviews = selectedReviews.includes(reviewId)
      ? selectedReviews.filter((id) => id !== reviewId)
      : [...selectedReviews, reviewId];

    setSelectedReviews(newSelectedReviews);
  };

  const handleSelectAll = () => {
    const filteredReviews = getFilteredReviews();
    if (selectedReviews.length === filteredReviews.length) {
      setSelectedReviews([]);
    } else {
      setSelectedReviews(filteredReviews.map((review) => Number(review.id)));
    }
  };

  const getFilteredReviews = () => {
    return currentTab === 'manager' ? postedContents : reviews;
  };

  const handleTabChange = (
    event: React.SyntheticEvent,
    newValue: 'editor' | 'manager',
  ) => {
    window.location.href =
      newValue === 'manager' ? '/posting-manage' : '/review-manage';
  };

  const fetchPostedContents = async (platform: string = '') => {
    if (!branchId) return;

    try {
      console.log('📊 포스팅 조회 시작:', { branchId, platform });

      const { apiGetPostedContents } = await import(
        '../../api/posted-content.api'
      );
      const postedContents = await apiGetPostedContents({
        branchId,
        platform: platform || undefined,
      });

      const formattedPostedContents = postedContents.map((content) => ({
        id: content.id,
        reviewId: content.reviewId,
        platform: content.platform,
        postId: content.postId,
        content: content.content,
        images: content.images || [],
        hashtags: content.hashtags || [],
        postedAt: content.postedAt,
        likes: content.likes || 0,
        comments: content.comments || 0,
        shares: content.shares || 0,
      }));

      formattedPostedContents.sort(
        (a, b) =>
          new Date(b.postedAt).getTime() - new Date(a.postedAt).getTime(),
      );

      setPostedContents(formattedPostedContents);
      console.log(`✅ 포스팅 조회 완료:`, formattedPostedContents.length, '개');
    } catch (error) {
      console.error('❌ 포스팅 조회 오류:', error);
      setPostedContents([]);
      toast.error('포스팅 데이터를 불러오는데 실패했습니다.');
    }
  };

  const generatePostContent = async () => {
    if (selectedReviews.length === 0) {
      toast.error('포스팅할 리뷰를 선택해주세요.');
      return;
    }

    setLoading(true);
    try {
      const selectedReviewsData = reviews.filter((r) =>
        selectedReviews.includes(Number(r.id)),
      );
      const combinedContent = selectedReviewsData
        .map(
          (review) =>
            `${review.serviceType || '서비스'}: ${review.reviewText || '리뷰 내용'}`,
        )
        .join('\n\n');

      try {
        const { gptPromptApi } = await import('../../api/gpt.api');
        const prompts = await gptPromptApi.getPrompts(branchId, 'diary');
        const activePrompt = prompts.find((p: any) => p.isActive) || prompts[0];

        if (activePrompt) {
          const gptResponse = await gptPromptApi.generateText({
            promptId: activePrompt.id,
            reviewContent: combinedContent,
            images: selectedReviewsData.flatMap((r) =>
              [r.beforeImageUrl, r.afterImageUrl].filter((img): img is string =>
                Boolean(img),
              ),
            ),
            apiKey:
              localStorage.getItem(`gpt_api_key_${branchId}`) || undefined,
          });

          if (gptResponse.success) {
            setPostContent({
              text: gptResponse.data.generatedText,
              images: selectedReviewsData.flatMap((r) =>
                [r.beforeImageUrl, r.afterImageUrl].filter(Boolean),
              ) as string[],
              hashtags: ['#추천', '#리뷰', '#맛집', '#만족'],
            });
            toast.success('GPT로 포스팅 내용을 자동 생성했습니다!');
          } else {
            throw new Error('GPT 생성 실패');
          }
        } else {
          throw new Error('활성화된 프롬포트가 없습니다');
        }
      } catch (gptError) {
        console.warn('GPT 생성 실패, 기본 템플릿 사용:', gptError);
        const defaultText = selectedReviewsData
          .map((review) => {
            const serviceType = review.serviceType || '서비스';
            const content = review.reviewText || '만족스러운 경험이었습니다';
            return `${serviceType} 후기: ${content}`;
          })
          .join('\n\n');
        setPostContent({
          text: defaultText + '\n\n좋은 서비스 받고 왔습니다 ✨',
          images: selectedReviewsData.flatMap((r) =>
            [r.beforeImageUrl, r.afterImageUrl].filter((img): img is string =>
              Boolean(img),
            ),
          ),
          hashtags: ['#추천', '#리뷰', '#만족'],
        });

        toast.info('기본 템플릿으로 포스팅 내용을 생성했습니다.');
      }

      setSelectedReviewImages(
        selectedReviews.map((reviewId) => ({
          reviewId,
          selectedImages: (() => {
            const review = reviews.find((r) => Number(r.id) === reviewId);
            return [review?.beforeImageUrl, review?.afterImageUrl].filter(
              (img): img is string => Boolean(img),
            );
          })(),
        })),
      );

      setPostEditorOpen(true);
    } catch (error) {
      console.error('포스팅 생성 오류:', error);
      toast.error('포스팅 생성 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleSocialAccountSave = async (
    platform: SocialPlatform,
    username: string,
    password: string,
  ) => {
    if (platform === SocialPlatform.INSTAGRAM) {
      await handleInstagramConnect(username, password);
      return;
    }

    try {
      setLoading(true);
      await socialAccountApi.createOrUpdateSocialAccount(branchId, {
        platform: platform,
        username,
        password,
      });

      setSocialAccounts((prev) =>
        prev.map((account) =>
          account.platform === platform
            ? { ...account, username, password: '', isConnected: true }
            : account,
        ),
      );

      toast.success(`${platform} 계정이 성공적으로 연결되었습니다!`);
      await fetchSocialAccounts();
    } catch (error) {
      console.error('소셜 계정 저장 오류:', error);
      toast.error(`${platform} 계정 연결에 실패했습니다.`);
    } finally {
      setLoading(false);
    }
  };

  const handleInstagramConnect = async (
    username: string,
    password: string,
    twoFactorCode?: string,
  ) => {
    try {
      setLoading(true);

      const response = await fetch(
        `${Config.API_HOST}/api/instagram/connect`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            access_token: localStorage.getItem('access_token') || '',
          },
          body: JSON.stringify({
            userId: branchId,
            username,
            password,
            twoFactorCode,
          }),
        },
      );

      const result = await response.json();

      if (response.ok && result.success) {
        toast.success(
          result.message || 'Instagram 계정이 성공적으로 연결되었습니다!',
        );
        setSocialAccounts((prev) =>
          prev.map((account) =>
            account.platform === SocialPlatform.INSTAGRAM
              ? {
                  ...account,
                  isConnected: true,
                  username: result.data?.username || username,
                }
              : account,
          ),
        );
      } else if (
        result.message &&
        result.message.includes('Facebook account')
      ) {
        toast.info(
          '🔗 이 Instagram 계정은 Facebook과 연결되어 있습니다. Facebook 로그인을 사용해주세요.',
        );
        setTimeout(() => {
          toast.info(
            '💡 상단의 "Facebook OAuth 연결" 버튼을 클릭하여 연결해보세요.',
          );
        }, 2000);
        return;
      } else if (result.message && result.message.includes('2차 인증')) {
        setPendingInstagramCredentials({ username, password });
        setInstagramTwoFactorOpen(true);
        toast.info(
          'Instagram 2차 인증이 필요합니다. 인증 코드를 입력해주세요.',
        );
        return;
      } else {
        throw new Error(result.message || 'Instagram 연결 실패');
      }
    } catch (error: any) {
      console.error('Instagram 연결 오류:', error);
      const errorMessage = error.message || '알 수 없는 오류';
      toast.error(`Instagram 연결 실패: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const handleInstagramTwoFactorSubmit = async () => {
    if (!pendingInstagramCredentials || !twoFactorCode.trim()) {
      toast.error('인증 정보 또는 코드를 입력해주세요.');
      return;
    }

    const isBackupCode = twoFactorCode.trim().length === 8;
    const codeType = isBackupCode ? '백업 코드' : '2차 인증 코드';

    try {
      setLoading(true);

      const requestBody: any = {
        userId: branchId,
        username: pendingInstagramCredentials.username,
        password: pendingInstagramCredentials.password,
        [isBackupCode ? 'backupCode' : 'twoFactorCode']: twoFactorCode.trim(),
      };

      const response = await fetch(
        `${Config.API_HOST}/api/instagram/connect`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            access_token: localStorage.getItem('access_token') || '',
          },
          body: JSON.stringify(requestBody),
        },
      );

      const result = await response.json();
      if (response.ok && result.success) {
        toast.success(
          isBackupCode
            ? '🎉 백업 코드로 Instagram 계정이 성공적으로 연결되었습니다!'
            : '🎉 Instagram 계정이 성공적으로 연결되었습니다!',
        );

        setSocialAccounts((prev) =>
          prev.map((account) =>
            account.platform === SocialPlatform.INSTAGRAM
              ? {
                  ...account,
                  isConnected: true,
                  username:
                    result.data?.username ||
                    pendingInstagramCredentials.username,
                }
              : account,
          ),
        );

        setInstagramTwoFactorOpen(false);
        setPendingInstagramCredentials(null);
        setTwoFactorCode('');
        await fetchSocialAccounts();
      } else {
        throw new Error(result.message || 'Instagram 연결 실패');
      }
    } catch (error: any) {
      console.error('Instagram 2차 인증 오류:', error);
      toast.error(
        `Instagram ${codeType} 인증 실패: ${error.message || '알 수 없는 오류'}`,
      );
    } finally {
      setLoading(false);
    }
  };

  const handleInstagramTwoFactorCancel = () => {
    setInstagramTwoFactorOpen(false);
    setPendingInstagramCredentials(null);
    setTwoFactorCode('');
    toast.info('Instagram 연결이 취소되었습니다.');
  };

  const handleOAuthConnect = async (platform: SocialPlatform) => {
    if (platform === SocialPlatform.FACEBOOK) {
      toast.error('Facebook 계정을 먼저 연결해주세요.');
      return;
    }

    if (platform === SocialPlatform.INSTAGRAM) {
      toast.info(
        'Instagram은 아래 사용자명/비밀번호 입력 후 "사용자명/비밀번호로 연결" 버튼을 클릭해주세요.',
      );
      return;
    }

    try {
      setLoading(true);
      const redirectUri = `${window.location.origin}/oauth-callback`;
      const { authUrl } = await oauthApi.getAuthUrl(platform, redirectUri);

      const popup = window.open(
        authUrl,
        'oauth',
        'width=600,height=600,scrollbars=yes,resizable=yes',
      );

      const messageListener = async (event: MessageEvent) => {
        if (event.origin !== window.location.origin) return;

        if (event.data.type === 'OAUTH_SUCCESS') {
          const { code, state } = event.data;
          try {
            const storedUser = localStorage.getItem('user');
            const userId = storedUser ? JSON.parse(storedUser).id : branchId;
            await oauthApi.handleCallback(platform, code, redirectUri, userId);
            toast.success(`${platform} 계정이 성공적으로 연결되었습니다!`);
            await fetchSocialAccounts();
          } catch (error) {
            console.error('OAuth 콜백 처리 오류:', error);
            toast.error(`${platform} 계정 연결에 실패했습니다.`);
          }
          window.removeEventListener('message', messageListener);
          popup?.close();
          setLoading(false);
        } else if (event.data.type === 'OAUTH_ERROR') {
          toast.error(`${platform} OAuth 인증에 실패했습니다.`);
          window.removeEventListener('message', messageListener);
          popup?.close();
          setLoading(false);
        }
      };

      window.addEventListener('message', messageListener);
      const checkClosed = setInterval(() => {
        if (popup?.closed) {
          clearInterval(checkClosed);
          window.removeEventListener('message', messageListener);
          setLoading(false);
        }
      }, 1000);

      toast.info(`${platform} OAuth 인증 창이 열렸습니다.`);
    } catch (error) {
      console.error('OAuth 연결 오류:', error);
      toast.error(`${platform} OAuth 연결에 실패했습니다.`);
      setLoading(false);
    }
  };

  const handlePost = async () => {
    if (selectedPlatforms.length === 0) {
      toast.warning('포스팅할 플랫폼을 선택해주세요.');
      return;
    }

    try {
      setLoading(true);

      if (selectedPlatforms.includes('meta-integration')) {
        const statusResponse = await fetch(
          `${Config.API_HOST}/api/facebook-oauth/status?userId=${branchId}`,
          {
            method: 'GET',
            headers: {
              access_token: localStorage.getItem('access_token') || '',
            },
          },
        );

        if (statusResponse.ok) {
          const statusData = await statusResponse.json();
          if (!statusData.isConnected) {
            throw new Error('Facebook 계정이 연결되지 않았습니다.');
          }
        }

        const selectedImageUrls: string[] = [];
        selectedReviewImages.forEach((selection) => {
          selectedImageUrls.push(...selection.selectedImages);
        });

        const facebookResponse = await fetch(
          `${Config.API_HOST}/api/facebook-oauth/post`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              access_token: localStorage.getItem('access_token') || '',
            },
            body: JSON.stringify({
              userId: branchId,
              message:
                postContent.text + '\n\n' + postContent.hashtags.join(' '),
              imageUrls: selectedImageUrls,
              reviewIds: selectedReviews,
            }),
          },
        );

        const facebookResult = await facebookResponse.json();
        if (facebookResponse.ok && facebookResult.success) {
          toast.success('📘 Facebook 포스팅이 완료되었습니다!');
        } else {
          toast.warning(
            `Facebook 포스팅 실패: ${facebookResult.message || '알 수 없는 오류'}`,
          );
        }

        setReviews((prevReviews) =>
          prevReviews.map((review) =>
            selectedReviews.includes(Number(review.id))
              ? {
                  ...review,
                  isPosted: true,
                  postedAt: new Date().toISOString(),
                  postedPlatforms: [
                    ...(review.postedPlatforms || []),
                    'Facebook',
                    'Instagram',
                  ],
                }
              : review,
          ),
        );

        await fetchReviews();
        setTimeout(() => {
          window.location.href = '/posting-manage';
        }, 1500);
      }

      if (selectedPlatforms.includes('threads')) {
        const statusResponse = await fetch(
          `${Config.API_HOST}/api/threads/status?userId=${branchId}`,
          {
            method: 'GET',
            headers: {
              access_token: localStorage.getItem('access_token') || '',
            },
          },
        );

        if (statusResponse.ok) {
          const statusData = await statusResponse.json();
          if (!statusData.isConnected) {
            throw new Error('Threads 계정이 연결되지 않았습니다.');
          }
        }

        const selectedImageUrls: string[] = [];
        selectedReviewImages.forEach((selection) => {
          selectedImageUrls.push(...selection.selectedImages);
        });

        const threadsResponse = await fetch(
          `${Config.API_HOST}/api/threads/post`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              access_token: localStorage.getItem('access_token') || '',
            },
            body: JSON.stringify({
              userId: branchId,
              text: postContent.text + '\n\n' + postContent.hashtags.join(' '),
              imageUrls: selectedImageUrls,
              reviewIds: selectedReviews,
            }),
          },
        );

        const responseData = await threadsResponse.json();
        if (threadsResponse.ok && responseData.success) {
          toast.success('🧵 Threads 포스팅이 완료되었습니다!');
          setReviews((prevReviews) =>
            prevReviews.map((review) =>
              selectedReviews.includes(Number(review.id))
                ? {
                    ...review,
                    isPosted: true,
                    postedAt: new Date().toISOString(),
                    postedPlatforms: [
                      ...(review.postedPlatforms || []),
                      'Threads',
                    ],
                  }
                : review,
            ),
          );
          await fetchReviews();
          setTimeout(() => {
            window.location.href = '/posting-manage';
          }, 1500);
        } else {
          throw new Error(
            responseData.message || 'Threads 포스팅에 실패했습니다.',
          );
        }
      }

      const otherPlatforms = selectedPlatforms.filter(
        (platform) =>
          platform !== 'meta-integration' &&
          platform !== 'threads' &&
          Object.values(SocialPlatform).includes(platform as SocialPlatform),
      ) as SocialPlatform[];

      if (otherPlatforms.length > 0) {
        const validation = await socialPostApi.validatePostingAccounts(
          branchId,
          otherPlatforms,
        );
        if (!validation.success) {
          toast.error(`계정 연결 오류: ${validation.message}`);
          return;
        }

        const selectedReviewsData = reviews.filter((review) =>
          selectedReviews.includes(Number(review.id)),
        );
        const allImageUrls: string[] = [];
        selectedReviewsData.forEach((review) => {
          if (review.beforeImageUrl) allImageUrls.push(review.beforeImageUrl);
          if (review.afterImageUrl) allImageUrls.push(review.afterImageUrl);
        });

        const reviewImageSelections = selectedReviewImages.map((selection) => ({
          reviewId: selection.reviewId,
          imageUrls: selection.selectedImages,
        }));

        const postRequest: CreateSocialPostRequest = {
          reviewIds: selectedReviews,
          platforms: otherPlatforms,
          postContent: postContent.text,
          hashtags: postContent.hashtags,
          imageUrls: allImageUrls.slice(0, 10),
          selectedReviewImages: reviewImageSelections,
        };

        await socialPostApi.createSocialPost(branchId, postRequest);
        toast.success(
          `${otherPlatforms.join(', ')}에 포스팅이 완료되었습니다!`,
        );
        setTimeout(() => {
          window.location.href = '/posting-manage';
        }, 1500);
      }

      await fetchReviews();
      setPostEditorOpen(false);
      setSelectedReviews([]);
      setSelectedReviewImages([]);
      setSelectedPlatforms([]);
    } catch (error) {
      console.error('포스팅 오류:', error);
      toast.error('포스팅에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleDisconnectAccount = async (platform: SocialPlatform) => {
    try {
      setLoading(true);

      if (platform === SocialPlatform.INSTAGRAM) {
        const response = await fetch(
          `${Config.API_HOST}/api/instagram/disconnect`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              access_token: localStorage.getItem('access_token') || '',
            },
            body: JSON.stringify({ userId: branchId }),
          },
        );

        const result = await response.json();
        if (!response.ok || !result.success) {
          throw new Error(result.message || 'Instagram 연결 해제 실패');
        }
      } else if (platform === SocialPlatform.FACEBOOK) {
        const response = await fetch(
          `${Config.API_HOST}/api/facebook-oauth/disconnect`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              access_token: localStorage.getItem('access_token') || '',
            },
            body: JSON.stringify({ userId: branchId }),
          },
        );

        const result = await response.json();
        if (!response.ok || !result.success) {
          throw new Error(result.message || 'Facebook 연결 해제 실패');
        }
      } else if (platform === SocialPlatform.THREADS) {
        const response = await fetch(
          `${Config.API_HOST}/api/threads-oauth/disconnect`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              access_token: localStorage.getItem('access_token') || '',
            },
            body: JSON.stringify({ userId: branchId }),
          },
        );

        const result = await response.json();
        if (!response.ok || !result.success) {
          throw new Error(result.message || 'Threads 연결 해제 실패');
        }
      } else {
        const accounts = await socialAccountApi.getSocialAccounts(branchId);
        const targetAccount = accounts.find((acc) => acc.platform === platform);
        if (targetAccount) {
          await socialAccountApi.deleteSocialAccount(targetAccount.id);
        }
      }

      setSocialAccounts((prev) =>
        prev.map((account) =>
          account.platform === platform
            ? { ...account, isConnected: false, username: '', password: '' }
            : account,
        ),
      );

      toast.success(`${platform} 계정 연결이 해제되었습니다.`);
    } catch (error) {
      console.error('계정 연결 해제 오류:', error);
      toast.error(`${platform} 계정 연결 해제에 실패했습니다.`);
    } finally {
      setLoading(false);
    }
  };

  const handleImageSelect = (reviewId: number, imageUrl: string) => {
    setSelectedReviewImages((prev) => {
      const existing = prev.find((item) => item.reviewId === reviewId);
      if (existing) {
        const isSelected = existing.selectedImages.includes(imageUrl);
        return prev.map((item) =>
          item.reviewId === reviewId
            ? {
                ...item,
                selectedImages: isSelected
                  ? item.selectedImages.filter((img) => img !== imageUrl)
                  : [...item.selectedImages, imageUrl],
              }
            : item,
        );
      } else {
        return [...prev, { reviewId, selectedImages: [imageUrl] }];
      }
    });
  };

  /**
   * 갤러리용 이미지 선택 핸들러
   */
  const handleGalleryImageSelect = (imageUrl: string) => {
    setSelectedImages((prev) =>
      prev.includes(imageUrl)
        ? prev.filter((url) => url !== imageUrl)
        : [...prev, imageUrl],
    );
  };

  const handleHashtagChange = (hashtag: string) => {
    setPostContent((prev) => ({
      ...prev,
      hashtags: prev.hashtags.includes(hashtag)
        ? prev.hashtags.filter((tag) => tag !== hashtag)
        : [...prev.hashtags, hashtag],
    }));
  };

  const handleAddHashtag = (newHashtag: string) => {
    if (
      newHashtag.trim() &&
      !postContent.hashtags.includes(newHashtag.trim())
    ) {
      setPostContent((prev) => ({
        ...prev,
        hashtags: [...prev.hashtags, newHashtag.trim()],
      }));
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const handleEditPost = async () => {
    if (!editingPost || !editPostContent.text.trim()) {
      toast.error('포스팅 내용을 입력해주세요.');
      return;
    }

    try {
      setLoading(true);
      const { apiUpdatePostedContent } = await import(
        '../../api/posted-content.api'
      );

      const updateData = {
        content: editPostContent.text,
        images: editPostContent.images,
        hashtags: editPostContent.hashtags,
        updateSocialMedia: true,
      };

      const result = (await apiUpdatePostedContent(
        editingPost.id,
        updateData,
      )) as any;
      const updatedPost = result.data || result;
      setPostedContents((prev) =>
        prev.map((post) =>
          post.id === editingPost.id ? { ...post, ...updatedPost } : post,
        ),
      );

      const socialUpdateResult = (result as any).socialMediaUpdate;
      if (socialUpdateResult && socialUpdateResult.success) {
        toast.success(
          `✅ ${editingPost.platform} 포스팅이 성공적으로 수정되었습니다!`,
        );
      } else {
        toast.warning(
          `⚠️ 데이터베이스는 수정되었지만 ${editingPost.platform} 포스팅 수정에 실패했습니다.`,
        );
      }

      setEditPostOpen(false);
      setEditingPost(null);
      setSelectedReviews([]);
    } catch (error: any) {
      console.error('포스팅 수정 오류:', error);
      toast.error(
        error.response?.data?.message ||
          error.message ||
          '포스팅 수정에 실패했습니다.',
      );
    } finally {
      setLoading(false);
    }
  };

  const handleEditHashtagChange = (hashtag: string) => {
    setEditPostContent((prev) => ({
      ...prev,
      hashtags: prev.hashtags.includes(hashtag)
        ? prev.hashtags.filter((h) => h !== hashtag)
        : [...prev.hashtags, hashtag],
    }));
  };

  const handleEditAddHashtag = (newHashtag: string) => {
    if (newHashtag && !editPostContent.hashtags.includes(newHashtag)) {
      setEditPostContent((prev) => ({
        ...prev,
        hashtags: [...prev.hashtags, newHashtag],
      }));
    }
  };

  const handleEditRemoveHashtag = (hashtagToRemove: string) => {
    setEditPostContent((prev) => ({
      ...prev,
      hashtags: prev.hashtags.filter((h) => h !== hashtagToRemove),
    }));
  };

  const handleDeletePosts = async () => {
    if (selectedReviews.length === 0) {
      toast.error('삭제할 포스팅을 선택해주세요.');
      return;
    }

    const selectedPosts = postedContents.filter((post) =>
      selectedReviews.includes(post.id),
    );
    const platformCounts = selectedPosts.reduce(
      (acc, post) => {
        acc[post.platform] = (acc[post.platform] || 0) + 1;
        return acc;
      },
      {} as Record<string, number>,
    );

    const platformText = Object.entries(platformCounts)
      .map(([platform, count]) => `${platform} ${Number(count)}개`)
      .join(', ');

    if (
      !window.confirm(
        `다음 포스팅을 삭제하시겠습니까?\n\n${platformText}\n\n⚠️ 실제 SNS에서도 삭제됩니다.`,
      )
    ) {
      return;
    }

    setLoading(true);
    try {
      const { apiDeletePostedContent } = await import(
        '../../api/posted-content.api'
      );
      let successCount = 0;
      let failCount = 0;

      for (const post of selectedPosts) {
        try {
          const result = await apiDeletePostedContent(post.id, true);
          if (result.success === false) {
            failCount++;
          } else {
            successCount++;
          }
        } catch (error: any) {
          console.error('❌ 포스팅 삭제 실패:', post.id, error);
          failCount++;
        }
      }

      if (successCount > 0 && failCount === 0) {
        toast.success(
          `✅ ${successCount}개의 포스팅이 성공적으로 삭제되었습니다!`,
        );
      } else if (successCount > 0 && failCount > 0) {
        toast.warning(
          `⚠️ ${successCount}개 삭제 성공, ${failCount}개 삭제 실패`,
        );
      } else {
        toast.error(`❌ 모든 포스팅 삭제에 실패했습니다.`);
      }

      if (successCount > 0) {
        setPostedContents((prev) =>
          prev.filter((post) => !selectedReviews.includes(post.id)),
        );
        setSelectedReviews([]);
      }
    } catch (error: any) {
      console.error('💥 포스팅 삭제 중 오류:', error);
      toast.error(
        `❌ 포스팅 삭제 중 오류가 발생했습니다: ${error.message || '네트워크 오류'}`,
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchGPTPrompts = async (type?: 'diary' | 'promotion') => {
    try {
      const prompts = await gptPromptApi.getPrompts(branchId, type);
      setGptPrompts(prompts);
      const activePrompt = prompts.find((p: any) => p.isActive);
      if (activePrompt) {
        setSelectedPrompt(activePrompt.id);
      }
    } catch (error) {
      console.error('GPT 프롬포트 조회 오류:', error);
      toast.error('GPT 프롬포트를 불러오는데 실패했습니다.');
    }
  };

  const handleGPTGenerate = async () => {
    if (selectedReviews.length === 0 || !selectedPrompt) {
      toast.error(
        selectedReviews.length === 0
          ? '포스팅을 생성할 리뷰를 선택해주세요.'
          : '사용할 프롬포트를 선택해주세요.',
      );
      return;
    }

    setGptGenerating(true);

    try {
      const selectedReviewData = reviews.filter((review) =>
        selectedReviews.includes(review.id),
      );
      const targetReview = selectedReviewData[0];

      const gptResponse = await gptPromptApi.generateText({
        promptId: selectedPrompt,
        reviewContent: targetReview.reviewText || '',
        images: [
          targetReview.beforeImageUrl,
          targetReview.afterImageUrl,
        ].filter(Boolean) as string[],
      });

      if (gptResponse.success && gptResponse.data) {
        setPostContent((prev) => ({
          ...prev,
          text: gptResponse.data.generatedText,
          images: [
            targetReview.beforeImageUrl,
            targetReview.afterImageUrl,
          ].filter(Boolean) as string[],
        }));
        setPostEditorOpen(true);
        toast.success(
          `🤖 AI ${gptPromptType === 'diary' ? '일기형' : '홍보형'} 포스팅이 생성되었습니다!`,
        );
      } else {
        throw new Error(gptResponse.message || 'GPT 생성에 실패했습니다.');
      }
    } catch (error: any) {
      console.error('GPT 포스팅 생성 오류:', error);
      toast.error(error.message || 'GPT 포스팅 생성에 실패했습니다.');
    } finally {
      setGptGenerating(false);
    }
  };

  const handleOpenPostEditor = () => {
    if (selectedReviews.length === 0) {
      toast.error('포스팅을 생성할 리뷰를 선택해주세요.');
      return;
    }

    fetchSocialAccounts(true);
    fetchGPTPrompts();
    setSelectedPrompt('diary_casual');

    const selectedReviewData = reviews.filter((r) =>
      selectedReviews.includes(r.id),
    );
    if (selectedReviewData.length > 0) {
      const targetReview = selectedReviewData[0];
      const defaultText = `${targetReview.serviceType || '서비스'} 후기\n\n${targetReview.reviewText || '만족스러운 경험이었습니다'}\n\n#제로살롱 #리뷰 #추천`;

      setPostContent({
        text: defaultText,
        images: [
          targetReview.beforeImageUrl,
          targetReview.afterImageUrl,
        ].filter(Boolean) as string[],
        hashtags: ['#추천', '#리뷰', '#만족'],
      });

      setSelectedReviewImages(
        selectedReviews.map((reviewId) => ({
          reviewId,
          selectedImages: (() => {
            const review = reviews.find((r) => Number(r.id) === reviewId);
            return [review?.beforeImageUrl, review?.afterImageUrl].filter(
              (img): img is string => Boolean(img),
            );
          })(),
        })),
      );
    }

    setPostEditorOpen(true);
  };

  const handleGPTPromptTypeChange = (newType: 'diary' | 'promotion') => {
    setGptPromptType(newType);
    setSelectedPrompt('');
    fetchGPTPrompts(newType);
  };

  const handleSyncPosts = async () => {
    if (!branchId) return;

    setSyncing(true);
    try {
      console.log('🔄 포스팅 동기화 시작:', { branchId });
      toast.info('🔄 SNS에서 포스팅을 가져오고 있습니다...');

      const { apiSyncPostsFromSocialMedia } = await import(
        '../../api/posted-content.api'
      );
      const result = await apiSyncPostsFromSocialMedia(branchId);

      if (result.success) {
        toast.success(
          `✅ ${result.syncedCount + result.reactivatedCount}개의 포스팅이 동기화되었습니다!`,
        );
        await fetchPostedContents(
          platformFilter === 'all' ? '' : platformFilter,
        );
      } else {
        toast.error(`❌ ${result.message || '포스팅 동기화에 실패했습니다.'}`);
      }
    } catch (error: any) {
      console.error('❌ 포스팅 동기화 오류:', error);
      toast.error(
        `❌ 동기화 중 오류가 발생했습니다: ${error.message || '네트워크 오류'}`,
      );
    } finally {
      setSyncing(false);
    }
  };

  const getDefaultPromptId = (type: string): string => {
    // 커스텀 프롬프트인 경우 그대로 반환
    if (type.startsWith('custom_')) {
      return type;
    }
    
    // 기본 프롬프트인 경우 기존 로직 사용
    if (type === 'diary' || type === 'promotion') {
      const savedPrompt = localStorage.getItem(
        `default${type === 'diary' ? 'Diary' : 'Promotion'}Prompt_${branchId}`,
      );
      return (
        savedPrompt ||
        (type === 'diary' ? 'diary_casual' : 'promotion_professional')
      );
    }
    
    // 알 수 없는 타입인 경우 기본값 반환
    return 'diary_casual';
  };

  const generateAutoPostContent = async (reviewIds: number[]) => {
    if (reviewIds.length === 0) return;

    setAiGenerating(true);

    try {
      const selectedReviewData = reviews.filter((review) =>
        reviewIds.includes(review.id),
      );
      if (selectedReviewData.length === 0) return;

      const targetReview = selectedReviewData[0];
      const savedPromptType = localStorage.getItem(`defaultPromptType_${branchId}`) || 'diary';
      const promptId = getDefaultPromptId(savedPromptType);

      console.log('🤖 자동 포스트 생성:', { savedPromptType, promptId, reviewId: targetReview.id });

      const reviewImages = [
        targetReview.beforeImageUrl,
        targetReview.afterImageUrl,
      ].filter(Boolean) as string[];

      // 간단한 리뷰 내용만 구성 (백엔드 프롬프트 템플릿 활용)
      let reviewContent = targetReview.reviewText || '만족스러운 서비스였습니다.';
      
      // 추가 정보가 있으면 자연스럽게 포함
      if (targetReview.serviceType) {
        reviewContent += ` (${targetReview.serviceType})`;
      }
      if (targetReview.menu) {
        reviewContent += ` - ${targetReview.menu}`;
      }

      console.log('📝 전달할 리뷰 내용 (간단화됨):', reviewContent);

      const gptResponse = await gptPromptApi.generateText({
        promptId,
        reviewContent, // 간단한 리뷰 텍스트만 전달
        images: reviewImages,
      });

      if (gptResponse.success && gptResponse.data) {
        setPostContent((prev) => ({
          ...prev,
          text: gptResponse.data.generatedText,
          images: [
            targetReview.beforeImageUrl,
            targetReview.afterImageUrl,
          ].filter(Boolean) as string[],
        }));
        toast.success('🤖 AI가 자동으로 포스트 내용을 생성했습니다!');
      } else {
        const defaultText = `${targetReview.serviceType || '서비스'} 후기\n\n${targetReview.reviewText || '만족스러운 경험이었습니다'}\n\n#추천 #리뷰 #만족`;
        setPostContent((prev) => ({
          ...prev,
          text: defaultText,
          images: [
            targetReview.beforeImageUrl,
            targetReview.afterImageUrl,
          ].filter(Boolean) as string[],
        }));
      }
    } catch (error: any) {
      console.error('자동 포스트 생성 오류:', error);
      if (reviewIds.length > 0) {
        const targetReview = reviews.find((r) => r.id === reviewIds[0]);
        if (targetReview) {
          const defaultText = `${targetReview.serviceType || '서비스'} 후기\n\n${targetReview.reviewText || '만족스러운 경험이었습니다'}\n\n#추천 #리뷰 #만족`;
          setPostContent((prev) => ({
            ...prev,
            text: defaultText,
            images: [
              targetReview.beforeImageUrl,
              targetReview.afterImageUrl,
            ].filter(Boolean) as string[],
          }));
        }
      }
    } finally {
      setAiGenerating(false);
    }
  };

  const handleAIPostingWithDefaultPrompt = async () => {
    if (selectedReviews.length === 0) {
      toast.error('포스팅을 생성할 리뷰를 선택해주세요.');
      return;
    }

    setGptGenerating(true);

    try {
      const selectedReviewData = reviews.filter((review) =>
        selectedReviews.includes(review.id),
      );
      const targetReview = selectedReviewData[0];
      const savedPromptType = localStorage.getItem(`defaultPromptType_${branchId}`) || 'diary';
      const promptId = getDefaultPromptId(savedPromptType);

      console.log('🤖 AI 포스팅 생성:', { savedPromptType, promptId, reviewId: targetReview.id });

      const reviewImages = [
        targetReview.beforeImageUrl,
        targetReview.afterImageUrl,
      ].filter(Boolean) as string[];

      // 간단한 리뷰 내용만 구성 (백엔드 프롬프트 템플릿 활용)
      let reviewContent = targetReview.reviewText || '만족스러운 서비스였습니다.';
      
      // 추가 정보가 있으면 자연스럽게 포함
      if (targetReview.serviceType) {
        reviewContent += ` (${targetReview.serviceType})`;
      }
      if (targetReview.menu) {
        reviewContent += ` - ${targetReview.menu}`;
      }

      console.log('📝 전달할 리뷰 내용 (간단화됨):', reviewContent);

      const gptResponse = await gptPromptApi.generateText({
        promptId,
        reviewContent, // 간단한 리뷰 텍스트만 전달
        images: reviewImages,
      });

      if (gptResponse.success && gptResponse.data) {
        setPostContent((prev) => ({
          ...prev,
          text: gptResponse.data.generatedText,
          images: [
            targetReview.beforeImageUrl,
            targetReview.afterImageUrl,
          ].filter(Boolean) as string[],
        }));
        
        // 프롬프트 타입에 따른 메시지 표시
        const typeDisplayName = savedPromptType.startsWith('custom_') 
          ? '커스텀 AI' 
          : savedPromptType === 'diary' ? '일기형' : '홍보형';
        
        toast.success(`🤖 AI 포스팅이 생성되었습니다! (${typeDisplayName} 프롬프트 사용)`);
      } else {
        throw new Error(
          gptResponse.message || 'AI 포스팅 생성에 실패했습니다.',
        );
      }
    } catch (error: any) {
      console.error('❌ AI 포스팅 생성 오류:', error);
      toast.error(error.message || 'AI 포스팅 생성에 실패했습니다.');
    } finally {
      setGptGenerating(false);
    }
  };

  const handleBlogEditorAIGenerate = async (
    selectedReviews: Review[],
    uploadedImages: string[],
    currentText: string,
    allSelectedImages: string[],
  ): Promise<string> => {
    try {
      setGptGenerating(true);

      // 입력 검증: 리뷰, 텍스트, 이미지 중 하나라도 있어야 함
      if (selectedReviews.length === 0 && !currentText.trim() && allSelectedImages.length === 0) {
        const errorMessage = '참고할 리뷰를 선택하거나 포스팅 내용을 작성하거나 이미지를 선택해주세요.';
        console.error('❌ AI 생성 검증 실패:', errorMessage);
        toast.error(errorMessage);
        return currentText; // 기존 텍스트 그대로 반환
      }

      const savedPromptType = localStorage.getItem(`defaultPromptType_${branchId}`) || 'diary';
      const promptId = getDefaultPromptId(savedPromptType);

      console.log('🤖 BlogEditor AI 생성:', { 
        savedPromptType, 
        promptId, 
        selectedReviewsCount: selectedReviews.length,
        textLength: currentText.length,
        imageCount: allSelectedImages.length
      });

      let reviewContent = '';

      if (selectedReviews.length > 0) {
        // 리뷰가 있는 경우: 간단한 리뷰 텍스트만 전달 (백엔드 프롬프트 템플릿 활용)
        const targetReview = selectedReviews[0];
        
        // 기본 리뷰 내용만 간단히 구성
        reviewContent = targetReview.reviewText || '만족스러운 서비스였습니다.';
        
        // 추가 정보가 있으면 자연스럽게 포함
        if (targetReview.serviceType) {
          reviewContent += ` (${targetReview.serviceType})`;
        }
        if (targetReview.menu) {
          reviewContent += ` - ${targetReview.menu}`;
        }
        
        // 사용자가 입력한 텍스트가 있으면 함께 포함
        if (currentText && currentText.trim()) {
          reviewContent = `${currentText.trim()}\n\n참고 리뷰: ${reviewContent}`;
        }
      } else if (currentText && currentText.trim()) {
        // 리뷰는 없고 텍스트만 있는 경우
        reviewContent = currentText.trim();
      } else {
        // 이미지만 있는 경우
        reviewContent = '오늘 방문한 곳에서 좋은 시간을 보냈습니다.';
      }

      console.log('📝 전달할 리뷰 내용 (간단화됨):', reviewContent.substring(0, 200) + '...');

      // 이미지 선택: 사용자가 선택한 이미지 우선, 없으면 리뷰 이미지
      let images = allSelectedImages;
      if (images.length === 0 && selectedReviews.length > 0) {
        const targetReview = selectedReviews[0];
        images = [targetReview.beforeImageUrl, targetReview.afterImageUrl].filter(Boolean) as string[];
      }

      const gptResponse = await gptPromptApi.generateText({
        promptId,
        reviewContent, // 간단한 리뷰 텍스트만 전달
        images,
      });

      if (gptResponse.success && gptResponse.data) {
        console.log('✅ AI 생성 성공:', {
          promptUsed: savedPromptType,
          generatedLength: gptResponse.data.generatedText.length
        });
        return gptResponse.data.generatedText;
      } else {
        throw new Error(
          gptResponse.message || 'AI 포스팅 생성에 실패했습니다.',
        );
      }
    } catch (error) {
      console.error('❌ GPT 생성 오류:', error);

      // 실패 시 fallback
      if (currentText && currentText.trim()) {
        // 기존 텍스트가 있으면 그대로 반환
        return currentText;
      } else if (selectedReviews.length > 0) {
        // 리뷰가 있으면 리뷰 기반 기본 템플릿 생성
        const reviewTexts = selectedReviews
          .map((r) => r.reviewText)
          .filter(Boolean);
        return `🌟 ${reviewTexts.join(' ')} \n\n#추천 #만족 #리뷰`;
      } else if (allSelectedImages.length > 0) {
        // 이미지만 있는 경우 이미지 기반 기본 템플릿 생성
        return `📸 오늘 방문한 곳에서 찍은 사진들이에요! 
정말 만족스러운 시간이었답니다 ✨

#맛집 #추천 #일상 #좋았어요`;
      } else {
        // 아무것도 없으면 기본 메시지
        return `🌟 오늘 방문한 곳에서 좋은 시간 보냈어요!

#추천 #만족`;
      }
    } finally {
      setGptGenerating(false);
    }
  };

  // BlogEditor 전용 포스팅 함수
  const handleBlogEditorPost = async (
    content: string,
    hashtags: string[],
    selectedReviewIds: number[],
    uploadedImages: string[],
  ) => {
    try {
      setLoading(true);
      console.log('🚀 BlogEditor 포스팅 시작:', {
        content: content.substring(0, 100) + '...',
        hashtags,
        selectedReviewIds,
        uploadedImages,
        branchId,
      });

      // 선택된 리뷰들의 이미지 수집
      const selectedReviewsData = reviews.filter((review) =>
        selectedReviewIds.includes(Number(review.id)),
      );
      const reviewImages: string[] = [];
      selectedReviewsData.forEach((review) => {
        if (review.beforeImageUrl) reviewImages.push(review.beforeImageUrl);
        if (review.afterImageUrl) reviewImages.push(review.afterImageUrl);
      });

      // 모든 이미지 합치기 (업로드된 이미지 + 리뷰 이미지)
      const allImages = [...uploadedImages, ...reviewImages];
      console.log('📷 수집된 이미지:', {
        uploadedImages: uploadedImages.length,
        reviewImages: reviewImages.length,
        total: allImages.length,
      });

      // 포스팅 메시지 생성 (내용 + 해시태그)
      const message = content + '\n\n' + hashtags.join(' ');

      let hasSuccess = false;
      const results: Array<{
        platform: string;
        success: boolean;
        error?: string;
      }> = [];

      // Meta 통합 포스팅 (Facebook + Instagram)
        try {
          console.log('📘 Meta 통합 포스팅 시작...', {
            userId: branchId,
            messageLength: message.length,
            imageCount: allImages.length,
          });

          // 먼저 연결 상태 확인
          const statusResponse = await fetch(
            `${Config.API_HOST}/api/facebook-oauth/status?userId=${branchId}`,
            {
              method: 'GET',
              headers: {
                access_token: localStorage.getItem('access_token') || '',
              },
            },
          );

          console.log(
            '🔍 Facebook 연결 상태 응답:',
            statusResponse.status,
            statusResponse.statusText,
          );

          if (!statusResponse.ok) {
            throw new Error(
              `Facebook 연결 상태 확인 실패: ${statusResponse.status}`,
            );
          }

          const statusData = await statusResponse.json();
          console.log('📊 Facebook 연결 상태:', statusData);

          if (!statusData.isConnected) {
            throw new Error(
              'Facebook 계정이 연결되지 않았습니다. 계정 설정에서 Facebook을 먼저 연결해주세요.',
            );
          }

          // Facebook + Instagram 통합 포스팅 요청
          const postPayload = {
            userId: branchId,
            message: message,
            imageUrls: allImages,
            reviewIds: selectedReviewIds,
          };

          console.log('📤 Meta 통합 포스팅 요청:', postPayload);

          const facebookResponse = await fetch(
            `${Config.API_HOST}/api/facebook-oauth/post`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                access_token: localStorage.getItem('access_token') || '',
              },
              body: JSON.stringify(postPayload),
            },
          );

          console.log(
            '📡 Meta 통합 포스팅 응답:',
            facebookResponse.status,
            facebookResponse.statusText,
          );

          const facebookResult = await facebookResponse.json();
          console.log('📋 Meta 통합 포스팅 결과:', facebookResult);

          if (facebookResponse.ok && facebookResult.success) {
            const successPlatforms = [];
            if (facebookResult.results?.facebook?.success) {
              successPlatforms.push('Facebook');
            }
            if (facebookResult.results?.instagram?.success) {
              successPlatforms.push('Instagram');
            }

            if (successPlatforms.length > 0) {
             
              hasSuccess = true;
              results.push({ platform: 'meta-integration', success: true });
            } else {
              const errors = facebookResult.errors || ['알 수 없는 오류'];
              toast.warning(`Meta 통합 포스팅 부분 실패: ${errors.join(', ')}`);
              results.push({
                platform: 'meta-integration',
                success: false,
                error: errors.join(', '),
              });
            }
          } else {
            const errorMsg =
              facebookResult.message ||
              facebookResult.errors?.join(', ') ||
              '알 수 없는 오류';
            throw new Error(errorMsg);
          }
        } catch (error: any) {
          console.error('❌ Meta 통합 포스팅 오류:', error);
          toast.error(`Meta 통합 포스팅 실패: ${error.message}`);
          results.push({
            platform: 'meta-integration',
            success: false,
            error: error.message,
          });
        }

      // Threads 포스팅
        try {
          console.log('🧵 Threads 포스팅 시작...', {
            userId: branchId,
            messageLength: message.length,
          });

          // Facebook OAuth 상태 확인 코드 제거됨

          const threadsPayload = {
            userId: branchId,
            content: message,
            imageUrls: allImages,
            reviewIds: selectedReviewIds,
          };

          console.log('📤 Threads 포스팅 요청:', threadsPayload);

          const threadsResponse = await fetch(
            `${Config.API_HOST}/api/threads-private/post`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                access_token: localStorage.getItem('access_token') || '',
              },
              body: JSON.stringify({
                userId: branchId,
                text: message,
                imageUrls: allImages,
              }),
            },
          );

          console.log(
            '📡 Threads 포스팅 응답:',
            threadsResponse.status,
            threadsResponse.statusText,
          );

          const threadsResult = await threadsResponse.json();
          console.log('📋 Threads 포스팅 결과:', threadsResult);

          if (threadsResponse.ok && threadsResult.success) {
            hasSuccess = true;
            results.push({ platform: 'threads', success: true });
          } else {
            const errorMsg = threadsResult.message || '알 수 없는 오류';
            throw new Error(errorMsg);
          }
        } catch (error: any) {
          console.error('❌ Threads 포스팅 오류:', error);
          toast.error(`Threads 포스팅 실패: ${error.message}`);
          results.push({
            platform: 'threads',
            success: false,
            error: error.message,
          });
        }

      console.log('📊 포스팅 결과 요약:', { hasSuccess, results });

      // 결과 분석
      const successResults = results.filter((r) => r.success);
      const failedResults = results.filter((r) => !r.success);
      const successPlatforms = successResults.map((r) => r.platform);
      const failedPlatforms = failedResults.map((r) => r.platform);

      console.log('📊 포스팅 결과 상세:', {
        successPlatforms,
        failedPlatforms,
        successCount: successResults.length,
        failedCount: failedResults.length
      });

      if (hasSuccess) {
        // 리뷰 상태 업데이트 (선택된 리뷰가 있을 때만)
        if (selectedReviewIds.length > 0) {
          setReviews((prevReviews) =>
            prevReviews.map((review) =>
              selectedReviewIds.includes(Number(review.id))
                ? {
                    ...review,
                    isPosted: true,
                    postedAt: new Date().toISOString(),
                    postedPlatforms: [
                      ...(review.postedPlatforms || []),
                      ...successPlatforms, // 성공한 플랫폼만 추가
                    ],
                  }
                : review,
            ),
          );
        }

        // 리뷰 목록 새로고침
        await fetchReviews();

        // 성공/실패 결과에 따른 상세 토스트 메시지
        if (failedResults.length === 0) {
          // 모든 플랫폼 성공
     
        } else {
          // 일부 성공, 일부 실패
          const platformEmojis: { [key: string]: string } = {
            'Facebook': '📘',
            'Instagram': '📸', 
            'Threads': '🧵',
            'meta-integration': '🔗'
          };

          // 성공 토스트
          const successMessage = successPlatforms
            .map(platform => `${platformEmojis[platform as keyof typeof platformEmojis] || '✅'} ${platform}`)
            .join(', ');
          toast.success(
            `🎉 일부 플랫폼 포스팅 성공!\n${successMessage}`,
            { duration: 4000 }
          );

          // 실패 토스트 (Instagram 화면 비율 문제 특별 처리)
          const failureMessages = failedResults.map(result => {
            const emoji = platformEmojis[result.platform as keyof typeof platformEmojis] || '❌';
            let errorMsg = result.error || '알 수 없는 오류';
            
            // Instagram 화면 비율 오류 감지
            if (result.platform === 'Instagram' && 
                (errorMsg.includes('aspect ratio') || errorMsg.includes('화면 비율'))) {
              errorMsg = '화면 비율 문제 (1:1, 4:5, 1.91:1 비율만 지원)';
            }
            
            return `${emoji} ${result.platform}: ${errorMsg}`;
          }).join('\n');

          toast.error(
            `⚠️ 일부 플랫폼 포스팅 실패\n${failureMessages}`,
            { duration: 6000 }
          );
        }

        // 포스팅 관리 페이지로 이동
        setTimeout(() => {
          window.location.href = '/posting-manage';
        }, 3000);
      } else {
        // 모든 플랫폼 실패
        const platformEmojis: { [key: string]: string } = {
          'Facebook': '📘',
          'Instagram': '📸', 
          'Threads': '🧵',
          'meta-integration': '🔗'
        };

        const failureMessages = failedResults.map(result => {
          const emoji = platformEmojis[result.platform as keyof typeof platformEmojis] || '❌';
          let errorMsg = result.error || '알 수 없는 오류';
          
          // Instagram 화면 비율 오류 감지
          if (result.platform === 'Instagram' && 
              (errorMsg.includes('aspect ratio') || errorMsg.includes('화면 비율'))) {
            errorMsg = '화면 비율 문제 - 이미지 크기를 조정해주세요';
          }
          
          return `${emoji} ${result.platform}: ${errorMsg}`;
        }).join('\n');

        toast.error(
          `❌ 모든 플랫폼 포스팅 실패\n\n${failureMessages}`,
          { duration: 8000 }
        );
      }
    } catch (error: any) {
      console.error('💥 BlogEditor 포스팅 오류:', error);
      toast.error(`포스팅 중 오류가 발생했습니다: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  if (!branchId) {
    return null;
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      {/* 포스팅 관리 헤더 (MUI Box/Typo) */}
      <div className="py-4 px-2 md:px-6 min-h-screen bg-gray-50">
        {/* 헤더 섹션 - MUI Card로 교체 */}
        <Card
          sx={{
            mb: 3,
            borderRadius: 4,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            bgcolor: '#42424a',
            overflow: 'hidden',
          }}
        >
          <CardContent sx={{ py: isMobile ? 3 : 5, px: isMobile ? 2 : 4 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: isMobile ? 'flex-start' : 'center',
                mb: 2,
                flexDirection: isMobile ? 'column' : 'row',
                gap: isMobile ? 2 : 0,
              }}
            >
              <Box sx={{ width: isMobile ? '100%' : 'auto' }}>
                <Typography
                  variant={isMobile ? 'h4' : 'h3'}
                  fontWeight='800'
                  sx={{ color: 'white', mb: 1 }}
                  style={{ color: 'white' }}
                >
                  ✍️ 리뷰 소셜 작성
                </Typography>
                <Typography
                  variant={isMobile ? 'body1' : 'h6'}
                  sx={{ color: 'rgba(255,255,255,0.95)', fontWeight: 400 }}
                >
                  고객 리뷰를 활용하여 매력적인 소셜 포스팅을 작성하세요
                </Typography>
                <Box
                  sx={{
                    mt: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: isMobile ? 1 : 2,
                    flexWrap: 'wrap',
                    flexDirection: isMobile ? 'column' : 'row',
                  }}
                >
                  {socialAccounts.map((account) => (
                    <Box
                      key={account.platform}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        px: isMobile ? 1.5 : 2,
                        py: isMobile ? 0.8 : 1,
                        bgcolor: account.isConnected
                          ? 'rgba(76, 175, 80, 0.2)'
                          : 'rgba(255, 152, 0, 0.2)',
                        borderRadius: 3,
                        border: `1px solid ${account.isConnected ? 'rgba(76, 175, 80, 0.3)' : 'rgba(255, 152, 0, 0.3)'}`,
                        backdropFilter: 'blur(10px)',
                        width: isMobile ? '100%' : 'auto',
                        justifyContent: isMobile ? 'flex-start' : 'center',
                      }}
                    >
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          bgcolor: account.isConnected ? '#4caf50' : '#ff9800',
                          boxShadow: `0 0 8px ${account.isConnected ? 'rgba(76, 175, 80, 0.6)' : 'rgba(255, 152, 0, 0.6)'}`,
                        }}
                      />
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        {account.platform === SocialPlatform.FACEBOOK ? (
                          <Facebook sx={{ fontSize: isMobile ? 18 : 20, color: '#1877F2' }} />
                        ) : (
                          <ThreadsIcon sx={{ fontSize: isMobile ? 18 : 20, color: 'white' }} />
                        )}
                        <Typography
                          variant={isMobile ? 'body2' : 'body2'}
                          sx={{ color: 'white', fontWeight: 600, fontSize: isMobile ? '0.8rem' : '0.875rem' }}
                          style={{ color: 'white' }}
                        >
                          {account.platform === SocialPlatform.FACEBOOK
                            ? 'Facebook'
                            : 'Threads'}
                          {account.isConnected ? ' 연결됨' : ' 연결 필요'}
                        </Typography>
                      </Box>
                      {account.isConnected && (
                        <Typography
                          variant='body2'
                          sx={{
                            color: 'rgba(255,255,255,0.8)',
                            fontSize: isMobile ? '0.7rem' : '0.8rem',
                          }}
                        >
                          ({account.username})
                        </Typography>
                      )}
                    </Box>
                  ))}
                  <Typography
                    variant='body2'
                    sx={{
                      color: 'rgba(255,255,255,0.7)',
                      fontSize: isMobile ? '0.75rem' : '0.85rem',
                      fontStyle: 'italic',
                      mt: isMobile ? 1 : 0,
                      textAlign: isMobile ? 'left' : 'center',
                    }}
                  >
                    Meta 통합 연결로 Facebook, Instagram, Threads 모두 사용 가능
                  </Typography>
                </Box>
              </Box>
              <Button
                variant='contained'
                startIcon={<Settings />}
                onClick={() => setSocialAccountsOpen(true)}
                sx={{
                  borderRadius: 3,
                  bgcolor: 'rgba(255,255,255,0.15)',
                  backdropFilter: 'blur(10px)',
                  color: 'rgb(255, 255, 255)',
                  fontWeight: 'bold',
                  px: isMobile ? 3 : 6,
                  py: isMobile ? 1 : 1.5,
                  border: '1px solid rgba(255,255,255,0.2)',
                  fontSize: isMobile ? '0.8rem' : '0.875rem',
                  width: isMobile ? '100%' : 'auto',
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.25)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 20px rgba(0,0,0,0.15)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                계정 설정 및 프롬포트 설정
              </Button>
            </Box>
          </CardContent>
        </Card>

        {/* AI 생성 중 알림 - 모바일 반응형 */}
        {aiGenerating && (
          <div className="mb-6 rounded-2xl shadow-lg bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-blue-200">
            <div className="p-4 md:p-6">
              <div className="flex items-center gap-3 justify-center">
                <div className="animate-spin">
                  <CircularProgress size={isMobile ? 24 : 28} className="text-blue-500" />
                </div>
                <div className="text-center md:text-left">
                  <h3 className="text-lg md:text-xl font-bold text-blue-600 mb-1">
                    🤖 AI가 포스팅 내용을 생성하고 있습니다...
                  </h3>
                  <p className="text-sm md:text-base text-gray-600">
                    선택된 리뷰를 바탕으로 매력적인 포스팅 내용을 자동 생성 중입니다.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 통합 포스팅 에디터 - 모바일 반응형 개선 */}
        <div className="bg-white rounded-2xl shadow-lg p-4 md:p-6">
          <BlogEditor
            branchId={branchId}
            reviews={reviews}
            onPost={handleBlogEditorPost}
            onAIGenerate={handleBlogEditorAIGenerate}
            socialAccounts={socialAccounts}
          />
        </div>
      </div>
      
      <Footer />

      {/* 소셜 계정 설정 다이얼로그 - 모바일 반응형 */}
      <SocialAccountConnector
        open={socialAccountsOpen}
        onClose={() => setSocialAccountsOpen(false)}
        branchId={branchId}
        onConnect={(platform, isConnected) => {
          console.log(`🔄 ${platform} 연결 상태 변경:`, isConnected);
          facebookStatusCache.invalidateCache(branchId);
          setTimeout(() => fetchSocialAccounts(true), 500);
        }}
      />
    </DashboardLayout>
  );
};

export default ReviewManage;
